import React, { useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withFieldProps } from 'hooks/useFieldProps';
import classnames from 'classnames';
import dialogActions from 'core/dialogs/actions';
import RawHtml from 'common/RawHtml';
import { FIELDS_ROOT } from 'config/api';
import useEmbedPdf from 'hooks/useEmbedPdf';
import { withStyles } from 'theme/withStyles';
import checkboxStyles from 'styles/checkbox';
import ErrorMsg from 'common/ErrorMsg';
import Help from 'common/Help';
import Tooltip from 'common/Tooltip';
import Spinner from 'common/Spinner';
import buttonLinkStyles from 'styles/buttonLink';
import radioStyles from 'styles/radio';
import visuallyHidden from 'styles/visuallyHidden';
import rootStyles from './styles/root';

const styles = (theme) => {
  const { links, oaoButtons: buttons, palette, spacing, typography } = theme;
  return {
    borderBottom: {
      '& .desc': {
        '& p': typography.p3,
      },
      borderBottom: `1px solid`,
      borderColor: ({ error }) => (error ? palette.error : palette.form.border),
      paddingBottom: spacing.lg,
    },
    btnDisclosure: {
      ...buttons.secondary,
      '&:hover': {
        color: buttons.secondary.hoverColor,
      },
      margin: 0,
    },
    checkboxDescription: {
      marginLeft: 30,
    },
    description: {
      ...typography.p,
      ...typography.p3,
      display: 'block',
      marginBottom: spacing.unit * 2,
    },
    disclosureRadioDividerLabel: {
      display: 'block',
      fontWeight: typography.fontWeights.bold,
      padding: [10, 0],
      textTransform: 'uppercase',
    },
    disclosureRadioWrapper: {
      position: 'relative',
    },
    embedPdf: {
      marginTop: spacing.sm,
    },
    flexbox: {
      '&.statusDiv a': {
        fontWeight: typography.fontWeights.bold,
      },
      display: 'flex',
    },
    handle: {
      display: 'none',
    },
    input: {
      ...checkboxStyles(theme),
      left: 0,
      position: 'absolute',
      top: 0,
    },
    label: {
      paddingLeft: 30,
    },
    openDialogLink: {
      ...buttonLinkStyles({ links }),
    },
    radio: {
      '& legend': {
        fontWeight: typography.fontWeights.bold,
      },
    },
    radioDescription: {
      '& ol': {
        '& ol': {
          listStyleType: 'lower-alpha',
        },
        listStyleType: 'decimal',
        marginBottom: 15,
        marginLeft: 15,
      },
    },
    radioInput: radioStyles(theme),
    root: {
      ...rootStyles({ spacing }),
      '& .embed-pdf': {
        paddingBottom: '75%',
      },
    },
    statusDiv: {
      '& fieldset': {
        '& input': {
          position: 'absolute',
        },
        '& label': {
          ...typography.p3,
          float: 'left',
          paddingLeft: spacing.sm + spacing.unit,
        },
        position: 'relative',
      },
      borderRight: `1px solid ${palette.form.border}`,
      marginRight: spacing.xs,
      paddingRight: spacing.xs,
      position: 'relative',
    },
    title: {
      ...typography.h3,
      '& *': {
        display: 'inline',
        margin: 0,
        padding: 0,
      },
      lineHeight: 'auto',
      verticalAlign: 'middle',
    },
    visuallyHidden,
  };
};

const Disclosure = ({
  classes,
  disclosureAcceptedLabel,
  disclosureRadioDividerLabel,
  disclosureViewAndAcceptLabel,
  disclosureViewDocumentLabel,
  error,
  falseLabel,
  fieldDescr,
  fieldDomId,
  fieldOptionalLabelSuffix,
  help,
  id,
  label,
  layout,
  loading,
  name,
  onChange,
  openDisclosure,
  readOnly,
  required,
  tooltip,
  trueLabel,
  validate,
  value,
}) => {
  const optionalLabel = !required
    ? `<small> ${fieldOptionalLabelSuffix || '(Optional)'}</small>`
    : '';
  const labelString = `${label}${optionalLabel}`;
  useEmbedPdf(
    `${FIELDS_ROOT}/${id}/disclosurePdf`,
    '#pdfEmbedId',
    {
      pdfOpenParams: {
        navpanes: 0,
        statusbar: 0,
        toolbar: 0,
        view: 'FitH',
      },
      title: label,
    },
    layout === 'inline',
  );
  const focusRef = useRef(null);
  const wrapRef = useRef(null);
  const dialogClickHandler = useCallback(
    (e) => {
      e.preventDefault();
      openDisclosure({
        onClose: () => focusRef?.current?.focus(),
      });
    },
    [openDisclosure],
  );
  useEffect(() => {
    const wrapNode = wrapRef.current;
    wrapNode.querySelectorAll('a, button').forEach((el) => {
      el.addEventListener('click', dialogClickHandler);
    });
    return () => {
      wrapNode.querySelectorAll('a, button').forEach((el) => {
        el.removeEventListener('click', dialogClickHandler);
      });
    };
  }, [dialogClickHandler]);
  return (
    <div ref={wrapRef} className={classnames('form-group', { error }, classes.root)}>
      <div className={classnames('border-bottom', classes.borderBottom)}>
        <fieldset>
          <legend className="header is-hidden">{name}</legend>
          <input
            id={`${fieldDomId}-a`}
            name={name}
            type="checkbox"
            disabled
            className={classnames('hidden input-handle')}
          />
        </fieldset>
        {(() => {
          switch (layout) {
            case 'button':
              return (
                <>
                  <RawHtml type="label" className={classes.title}>
                    {labelString}
                  </RawHtml>
                  <RawHtml
                    className={classnames('desc', { greyed: readOnly }, classes.description)}
                  >
                    {fieldDescr}
                  </RawHtml>
                  <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={3} />
                  <Help help={help} id={`help-${name}`} />
                  <div className={classnames('form-group', classes.flexbox)}>
                    {value ? (
                      <>
                        <div className={classnames('checkbox statusDiv', classes.statusDiv)}>
                          <fieldset>
                            <legend className="header is-hidden">{name}</legend>
                            <input
                              type="checkbox"
                              aria-describedby={
                                classnames({
                                  [`help-${name}`]: help,
                                  [`error-${name}`]: error,
                                }) || undefined
                              }
                              id={fieldDomId}
                              name={name}
                              disabled
                              checked
                              ref={focusRef}
                              className={classes.input}
                            />{' '}
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label className={classes.title} htmlFor={fieldDomId}>
                              {disclosureAcceptedLabel}
                            </label>
                          </fieldset>
                        </div>
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <button
                          onClick={dialogClickHandler}
                          type="button"
                          className={classnames('buttonLink', classes.openDialogLink)}
                        >
                          {disclosureViewDocumentLabel}
                          <span className={classnames('hidden', classes.visuallyHidden)}>
                            {` - ${labelString}`}
                          </span>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className={classnames('btn btn-mobile', classes.btnDisclosure)}
                        id={fieldDomId}
                        name={name}
                        onClick={dialogClickHandler}
                        ref={focusRef}
                      >
                        <i className="icon-arrow-thin-right" aria-hidden="true" />
                        {disclosureViewAndAcceptLabel}
                      </button>
                    )}
                  </div>
                </>
              );
            case 'inline':
              return (
                <div className="inline-pdf">
                  <RawHtml type="label" htmlFor={fieldDomId} className={classes.title}>
                    {labelString}
                  </RawHtml>
                  {fieldDescr && (
                    <>
                      <RawHtml className={classnames('desc', classes.description)}>
                        {fieldDescr}
                      </RawHtml>
                      <br />
                    </>
                  )}
                  <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={3} />
                  <Help help={help} id={`help-${name}`} />
                  <div id="pdfEmbedId" className={classnames('embed-pdf', classes.embedPdf)} />
                </div>
              );
            case 'radio': {
              return (
                <fieldset
                  id={`${fieldDomId}`}
                  className={classnames(
                    'form-inline',
                    'form-group',
                    'disclosure-radio',
                    { error, loading },
                    classes.root,
                    classes.radio,
                  )}
                >
                  <legend className={classes.legend}>
                    <RawHtml type="span">{labelString}</RawHtml>
                    <Tooltip
                      tooltip={tooltip}
                      labeledBy={`label-${fieldDomId}`}
                      handleYOffset={2}
                    />
                    <Help help={help} id={`help-${name}`} />
                    {required && (
                      <span className={classnames('hidden', classes.visuallyHidden)}>Required</span>
                    )}
                  </legend>
                  <RawHtml
                    type="div"
                    htmlFor={fieldDomId}
                    className={classnames(
                      'radio-description',
                      classes.description,
                      classes.radioDescription,
                    )}
                  >
                    {fieldDescr}
                  </RawHtml>
                  <Spinner loading={loading} />
                  <div className={classnames('radio form-group', classes.radio)}>
                    <div
                      className={classnames(
                        'disclosureRadioWrapper',
                        classes.disclosureRadioWrapper,
                      )}
                    >
                      <input
                        type="radio"
                        aria-describedby={
                          classnames({
                            [`help-${name}`]: help,
                            [`error-${name}`]: error,
                          }) || undefined
                        }
                        name={fieldDomId}
                        id={`${fieldDomId}-true`}
                        checked={value === true}
                        onChange={() => {
                          if (!readOnly) {
                            onChange(name, true);
                            validate();
                          }
                        }}
                        readOnly={readOnly}
                        className={classes.radioInput}
                        value
                      />
                      <RawHtml
                        type="label"
                        htmlFor={`${fieldDomId}-true`}
                        className={classes.label}
                      >
                        {trueLabel}
                      </RawHtml>
                    </div>
                    <span
                      className={classnames(
                        'disclosureRadioDividerLabel',
                        classes.disclosureRadioDividerLabel,
                      )}
                    >
                      {disclosureRadioDividerLabel}
                    </span>
                    <div
                      className={classnames(
                        'disclosureRadioWrapper',
                        classes.disclosureRadioWrapper,
                      )}
                    >
                      <input
                        type="radio"
                        aria-describedby={
                          classnames({
                            [`help-${name}`]: help,
                            [`error-${name}`]: error,
                          }) || undefined
                        }
                        name={fieldDomId}
                        id={`${fieldDomId}-false`}
                        checked={value === false}
                        onChange={() => {
                          if (!readOnly) {
                            onChange(name, false);
                            validate();
                          }
                        }}
                        readOnly={readOnly}
                        className={classes.radioInput}
                        value
                      />
                      <RawHtml
                        type="label"
                        htmlFor={`${fieldDomId}-false`}
                        className={classes.label}
                      >
                        {falseLabel}
                      </RawHtml>
                    </div>
                  </div>
                </fieldset>
              );
            }
            case 'checkbox':
              return (
                <>
                  <RawHtml type="label" htmlFor={fieldDomId} className={classes.label}>
                    {labelString}
                  </RawHtml>
                  <Spinner loading={loading} />
                  <div className={classnames('checkbox', 'form-group', { error })}>
                    <fieldset>
                      <input
                        ref={focusRef}
                        type="checkbox"
                        id={fieldDomId}
                        name={name}
                        checked={value || false}
                        aria-describedby={
                          classnames({
                            [`help-${name}`]: help,
                            [`error-${name}`]: error,
                          }) || undefined
                        }
                        onChange={() => {
                          /* to help trigger the validation error */
                          onChange(name, value && required ? '' : !value);
                          validate();
                        }}
                        disabled={readOnly}
                        className={classes.input}
                      />
                      <RawHtml
                        type="label"
                        htmlFor={fieldDomId}
                        className={classnames(
                          'checkbox-description',
                          classes.description,
                          classes.checkboxDescription,
                        )}
                      >
                        {fieldDescr}
                      </RawHtml>
                    </fieldset>
                  </div>
                </>
              );
            default:
              return value ? (
                <>
                  <RawHtml type="label" className={classes.title}>
                    {labelString}
                  </RawHtml>
                  <Help help={help} id={`help-${name}`} />
                  <RawHtml className={classnames('desc', classes.description)}>
                    {fieldDescr}
                  </RawHtml>
                  <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={3} />
                  <Help help={help} id={`help-${name}`} />
                  <div className={classes.flexbox}>
                    <div className={classnames('checkbox form-group statusDiv', classes.statusDiv)}>
                      <fieldset>
                        <legend className="header is-hidden">{name}</legend>
                        <input
                          id={fieldDomId}
                          name={name}
                          aria-describedby={
                            classnames({
                              [`help-${name}`]: help,
                              [`error-${name}`]: error,
                            }) || undefined
                          }
                          type="checkbox"
                          disabled
                          checked
                          ref={focusRef}
                          className={classes.input}
                        />{' '}
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor={fieldDomId}>{disclosureAcceptedLabel}</label>
                      </fieldset>
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <button
                      type="button"
                      className={classnames('buttonLink viewLink', classes.openDialogLink)}
                      onClick={dialogClickHandler}
                    >
                      {disclosureViewDocumentLabel}
                      <span className={classnames('hidden', classes.visuallyHidden)}>
                        {` - ${labelString}`}
                      </span>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <RawHtml type="label" className={classes.title}>
                    {labelString}
                  </RawHtml>
                  <Tooltip tooltip={tooltip} labeledBy={`label-${fieldDomId}`} handleYOffset={3} />
                  <Help help={help} id={`help-${name}`} />
                  <RawHtml
                    className={classnames('desc', { greyed: readOnly }, classes.description)}
                  >
                    {fieldDescr}
                  </RawHtml>
                  <br />
                  <div className={classnames('statusDiv', classes.flexbox)}>
                    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                    <button
                      type="button"
                      ref={focusRef}
                      onClick={dialogClickHandler}
                      className={classnames('buttonLink', classes.openDialogLink)}
                    >
                      {disclosureViewAndAcceptLabel}
                    </button>
                  </div>
                </>
              );
          }
        })()}
        <ErrorMsg name={name} />
      </div>
    </div>
  );
};

Disclosure.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  disclosureAcceptedLabel: PropTypes.string,
  disclosureRadioDividerLabel: PropTypes.string,
  disclosureViewAndAcceptLabel: PropTypes.string,
  disclosureViewDocumentLabel: PropTypes.string,
  error: PropTypes.bool.isRequired,
  falseLabel: PropTypes.string,
  fieldDescr: PropTypes.string.isRequired,
  fieldDomId: PropTypes.string.isRequired,
  fieldOptionalLabelSuffix: PropTypes.string,
  help: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  layout: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  openDisclosure: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  required: PropTypes.bool.isRequired,
  tooltip: PropTypes.string.isRequired,
  trueLabel: PropTypes.string,
  validate: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
};

Disclosure.defaultProps = {
  disclosureAcceptedLabel: 'Accepted',
  disclosureRadioDividerLabel: 'or',
  disclosureViewAndAcceptLabel: 'View and Accept',
  disclosureViewDocumentLabel: 'View Document',
  falseLabel: '',
  fieldOptionalLabelSuffix: '',
  layout: null,
  trueLabel: '',
};

const DisclosureStyled = withStyles(styles)(Disclosure);

const mapStateToProps = ({ fields, resources }, { name }) => ({
  disclosureAcceptedLabel: resources.disclosureAcceptedLabel,
  disclosureRadioDividerLabel: resources.disclosureRadioDividerLabel,
  disclosureViewAndAcceptLabel: resources.disclosureViewAndAcceptLabel,
  disclosureViewDocumentLabel: resources.disclosureViewDocumentLabel,
  falseLabel: fields[name].falseLabel,
  fieldOptionalLabelSuffix: resources.fieldOptionalLabelSuffix,
  layout: fields[name].layout,
  trueLabel: fields[name].trueLabel,
});

const mapDispatchToProps = (dispatch, { id, layout, name, readOnly }) => ({
  openDisclosure: (params) =>
    dispatch(
      dialogActions.open('disclosure', {
        ...params,
        clientId: name,
        id,
        readOnly: layout === 'checkbox' ? true : readOnly,
      }),
    ),
});

export default withFieldProps(mapStateToProps, mapDispatchToProps)(DisclosureStyled);
